/** 余额与充值 (公司管理员): 余额 / 充值弹窗 / 充值订单 / 余额流水 */
import { useState } from "react";
import { Plus, Wallet } from "lucide-react";
import { errorDetail } from "@/lib/api-client";
import {
  Alert, Badge, Button, Card, PageHeader, Pagination, Spinner,
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow, type BadgeProps,
} from "@/components/ui";
import { useAuthStore } from "@/stores/auth-store";
import { useCurrentUser } from "@/features/auth/hooks";
import { useLedger, useRecharges } from "../hooks";
import { RechargeModal } from "../components/RechargeModal";
import { fenToYuan, fmtTime } from "../utils";

const PAGE_SIZE = 20;

const REASON_LABEL: Record<string, string> = {
  recharge: "充值",
  quote: "智能报价",
  image: "模板出图",
  adjust: "人工调整",
};

const ORDER_STATUS: Record<string, { label: string; variant: BadgeProps["variant"] }> = {
  pending: { label: "待支付", variant: "warning" },
  paid: { label: "已支付", variant: "success" },
  failed: { label: "失败", variant: "neutral" },
};

function LoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-8 text-center text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-2"><Spinner /> 加载中…</span>
      </td>
    </tr>
  );
}

export function BillingPage() {
  const companyName = useAuthStore((s) => s.user?.company_name);
  // 本公司生效单价 (超管可按公司调整, 未调整为系统默认), 只读展示
  const me = useCurrentUser().data;
  const [page, setPage] = useState(1);
  const ledger = useLedger(PAGE_SIZE, (page - 1) * PAGE_SIZE);
  const orders = useRecharges(50);
  const [showRecharge, setShowRecharge] = useState(false);

  const loadErr = ledger.error ?? orders.error;

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader title="余额与充值" description="公司余额、充值订单与余额流水" />

      {loadErr && <Alert>{errorDetail(loadErr, "加载失败")}</Alert>}

      <Card className="flex flex-wrap items-center justify-between gap-4 p-4 sm:p-5">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-soft">
            <Wallet className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-xs text-muted-foreground">{companyName ?? "本公司"} · 当前余额</div>
            <div className="text-2xl font-semibold tabular-nums text-foreground sm:text-3xl">
              ¥{ledger.data ? fenToYuan(ledger.data.balance_fen) : "--"}
            </div>
            {me?.credit_price_quote_fen != null && me.credit_price_image_fen != null && (
              <div className="mt-1 text-xs tabular-nums text-muted-foreground">
                当前计费：智能报价 ¥{fenToYuan(me.credit_price_quote_fen)}/次 · AI 画图 ¥{fenToYuan(me.credit_price_image_fen)}/张
              </div>
            )}
          </div>
        </div>
        <Button onClick={() => setShowRecharge(true)} icon={<Plus />}>
          充值
        </Button>
      </Card>

      <section className="space-y-2">
        <h2 className="text-section">充值订单</h2>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>订单号</TableHead>
              <TableHead numeric>金额 (元)</TableHead>
              <TableHead className="w-20">状态</TableHead>
              <TableHead>创建时间</TableHead>
              <TableHead>支付时间</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.isLoading && <LoadingRow colSpan={5} />}
            {orders.data && orders.data.data.length === 0 && (
              <TableEmpty colSpan={5} title="暂无充值记录" hint="点击上方「充值」创建第一笔订单" />
            )}
            {orders.data?.data.map((o) => {
              const st = ORDER_STATUS[o.status] ?? ORDER_STATUS.failed;
              return (
                <TableRow key={o.merchant_order_no}>
                  <TableCell className="whitespace-nowrap font-mono text-xs">{o.merchant_order_no}</TableCell>
                  <TableCell numeric className="font-medium">{fenToYuan(o.amount_fen)}</TableCell>
                  <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                  <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{fmtTime(o.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{fmtTime(o.paid_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </section>

      <section className="space-y-2">
        <h2 className="text-section">余额流水</h2>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>时间</TableHead>
              <TableHead>类型</TableHead>
              <TableHead numeric>变动 (元)</TableHead>
              <TableHead numeric>余额 (元)</TableHead>
              <TableHead>关联</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ledger.isLoading && <LoadingRow colSpan={5} />}
            {ledger.data && ledger.data.data.length === 0 && <TableEmpty colSpan={5} title="暂无流水" />}
            {ledger.data?.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{fmtTime(r.created_at)}</TableCell>
                <TableCell className="whitespace-nowrap">{REASON_LABEL[r.reason] ?? r.reason}</TableCell>
                <TableCell numeric className={r.delta_fen >= 0 ? "font-medium text-success-strong" : "font-medium text-danger-strong"}>
                  {r.delta_fen >= 0 ? "+" : ""}{fenToYuan(r.delta_fen)}
                </TableCell>
                <TableCell numeric>{fenToYuan(r.balance_after)}</TableCell>
                <TableCell className="max-w-[16rem] truncate font-mono text-xs text-muted-foreground">{r.ref || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {ledger.data && (
          <Pagination total={ledger.data.total} page={page} pageSize={PAGE_SIZE} onChange={setPage} />
        )}
      </section>

      {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} />}
    </div>
  );
}
